<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import Button from "./Button.vue";
import QrCode from "./QrCode.vue";
import RecoveryCodes from "./RecoveryCodes.vue";
import { api } from "../api/client";
import { errorText } from "../api/errors";
import { toast } from "../composables/feedback";
import { session } from "../composables/session";
import { t } from "../i18n";

/**
 * Turning two-step verification on: a QR code (and the key to type) for the authenticator app,
 * the first code to confirm, then the recovery codes. Used in "Minha conta" and on the page
 * managers see when the platform requires it.
 */
defineProps<{ cancellable?: boolean }>();
const emit = defineEmits<{ done: []; cancel: [] }>();

const setup = ref<{ secret: string; otpauthUrl: string } | null>(null);
const code = ref("");
const busy = ref(false);
const recoveryCodes = ref<string[] | null>(null);
const codeInput = ref<HTMLInputElement>();

/** The key grouped by four, as authenticator apps show it for typing. */
const groupedSecret = computed(
  () => setup.value?.secret.match(/.{1,4}/g)?.join(" ") ?? "",
);

onMounted(async () => {
  try {
    setup.value = await api("/v1/auth/mfa/setup", { method: "POST", body: {} });
    await nextTick();
    codeInput.value?.focus();
  } catch (err) {
    toast(errorText(err));
    emit("cancel");
  }
});

async function confirm() {
  busy.value = true;
  try {
    const r = await api<{ recoveryCodes: string[] }>("/v1/auth/mfa/enable", {
      method: "POST",
      body: { code: code.value.trim() },
    });
    recoveryCodes.value = r.recoveryCodes;
    if (session.me) {
      session.me.user.mfaEnabled = true;
      session.me.mfaSetupRequired = false;
    }
  } catch (err) {
    toast(errorText(err));
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <RecoveryCodes
    v-if="recoveryCodes"
    :codes="recoveryCodes"
    @done="emit('done')"
  />
  <form v-else-if="setup" class="account-section" @submit.prevent="confirm">
    <h3>{{ t("account.mfa.setup_title") }}</h3>
    <ol class="account-steps small">
      <li>{{ t("account.mfa.setup_step1") }}</li>
      <li>{{ t("account.mfa.setup_step2") }}</li>
      <li>{{ t("account.mfa.setup_step3") }}</li>
    </ol>
    <div class="account-qr">
      <QrCode
        :value="setup.otpauthUrl"
        :size="180"
        :label="t('account.mfa.qr_label')"
      />
      <div class="small">
        <span class="muted">{{ t("account.mfa.manual") }}</span>
        <code class="account-secret">{{ groupedSecret }}</code>
      </div>
    </div>
    <label class="wa-field"
      ><span>{{ t("auth.mfa.code") }}</span
      ><input
        ref="codeInput"
        v-model="code"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="6"
        placeholder="000000"
        class="auth-code"
        required
    /></label>
    <div class="row account-actions">
      <span class="grow" />
      <Button
        v-if="cancellable"
        variant="secondary"
        type="button"
        @click="emit('cancel')"
        >{{ t("common.cancel") }}</Button
      >
      <Button type="submit" :loading="busy">{{
        t("account.mfa.confirm")
      }}</Button>
    </div>
  </form>
  <p v-else class="small muted">{{ t("inbox.loading") }}</p>
</template>
